/**
 * Admin Users Page
 * Admin panel for managing users with search, filters, and actions
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Input,
  Select,
  Button,
  Text,
  useToast,
  Spinner,
  HStack,
  VStack,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { Search, RefreshCw } from 'lucide-react';
import { adminService, type AdminUser, type GetUsersParams } from '../../services/AdminService';
import UserTable from '../../components/admin/UserTable';

export const UsersPage: React.FC = () => {
  const toast = useToast();

  // State
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'blocked'>('all');

  // Load users
  const loadUsers = async (params?: Partial<GetUsersParams>) => {
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        search: searchQuery,
        role: roleFilter,
        status: statusFilter,
        page: pagination.page,
        limit: pagination.limit,
        ...params,
      });

      setUsers(response.users);
      setPagination(response.pagination);
    } catch (error: any) {
      toast({
        title: 'Error loading users',
        description: error.response?.data?.detail || 'Failed to load users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadUsers();
  }, []);

  // Handle search
  const handleSearch = () => {
    loadUsers({ page: 1 });
  };

  // Handle filter changes
  const handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value as any);
    setPagination({ ...pagination, page: 1 });
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value as any);
    setPagination({ ...pagination, page: 1 });
  };

  // Apply filters when they change
  useEffect(() => {
    loadUsers();
  }, [roleFilter, statusFilter]);

  // Handle refresh
  const handleRefresh = () => {
    loadUsers({ page: pagination.page });
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    loadUsers({ page: newPage });
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="flex-end" align="center">
          <Button
            leftIcon={<RefreshCw size={16} />}
            onClick={handleRefresh}
            size="sm"
            variant="outline"
          >
            Refresh
          </Button>
        </Flex>

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="md" shadow="sm">
          <HStack spacing={4}>
            {/* Search */}
            <InputGroup flex={2}>
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search by email, username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </InputGroup>

            {/* Role Filter */}
            <Select
              value={roleFilter}
              onChange={handleRoleFilterChange}
              flex={1}
            >
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Select>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              flex={1}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </Select>

            {/* Search Button */}
            <Button colorScheme="blue" onClick={handleSearch}>
              Search
            </Button>
          </HStack>
        </Box>

        {/* Stats */}
        <HStack spacing={4}>
          <Box bg="white" p={4} borderRadius="md" shadow="sm" flex={1}>
            <Text fontSize="sm" color="gray.600">
              Total Users
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {pagination.total}
            </Text>
          </Box>
          <Box bg="white" p={4} borderRadius="md" shadow="sm" flex={1}>
            <Text fontSize="sm" color="gray.600">
              Current Page
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {pagination.page} / {pagination.pages}
            </Text>
          </Box>
        </HStack>

        {/* Users Table */}
        <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
          {loading ? (
            <Flex justify="center" align="center" py={20}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : users.length === 0 ? (
            <Flex justify="center" align="center" py={20}>
              <Text fontSize="lg" color="gray.500">
                No users found
              </Text>
            </Flex>
          ) : (
            <>
              <UserTable users={users} onRefresh={handleRefresh} />

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.600">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      isDisabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          variant={pagination.page === pageNum ? 'solid' : 'outline'}
                          colorScheme={pagination.page === pageNum ? 'blue' : 'gray'}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      isDisabled={pagination.page === pagination.pages}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default UsersPage;
